import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Badge, Input, LoadingSpinner } from '@/components/common';
import { useCanState } from '@/hooks/useEmployeeRights';
import { useGlobalSearch, GLOBAL_SEARCH_MIN_LENGTH } from '@/hooks/useGlobalSearch';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/utils';

import {
  clampSelection,
  groupOffsets,
  moveSelection,
  resultHref,
  resultKey,
  resultStatusLabel,
} from './searchResults';
import type { GlobalSearchResult } from './types';

interface GlobalSearchProps {
  /** Extra classes for the inline (>= sm) field wrapper. */
  className?: string;
  placeholder?: string;
}

/**
 * The header's cross-module search.
 *
 * Renders twice over, by breakpoint, because a 320px header has no room for a
 * text field next to the avatar and quick-action button: an inline combobox at
 * `sm` and up, and a magnifier that opens a full-screen sheet below it. Only
 * one of the two is ever mounted, so element ids stay unique and there is only
 * ever one input to focus.
 *
 * Keyboard contract (ARIA combobox + listbox):
 *   ArrowDown / ArrowUp  move through the FLATTENED results, wrapping
 *   Enter                open the active row (or the first, if none is active)
 *   Escape               dismiss the panel; a second Escape clears the query
 */
export function GlobalSearch({ className, placeholder = 'Search everything…' }: GlobalSearchProps) {
  const navigate = useNavigate();
  const baseId = useId().replace(/:/g, '');
  const listboxId = `${baseId}-listbox`;

  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { groups, flatResults, isLoading, error, tooShort, hasSearched, resultTerm } =
    useGlobalSearch(term);

  const offsets = useMemo(() => groupOffsets(groups), [groups]);

  // B4/D2: `/posps/:id` is guarded by a role list AND `channels: read`, and
  // channels read is an ordinary revocable cell (rights_catalog.py locks only
  // CREATE/EDIT/DELETE for a POSP). `resultHref` is React-free, so the rights
  // answer has to be passed in: without it a POSP or employee whose channels
  // read had been revoked was still handed a live link that only lands on
  // /unauthorized. The role argument stays `undefined` on purpose -- that is
  // `resultHref`'s "read the auth store yourself" case, which the pure tests
  // pin -- while the third argument is the cell this component can see.
  //
  // `useCanState`, not `useCan`: `useCan` throws `isError` away, and a FAILED
  // `/employee-rights/me/` reads as `false` in every cell. A row closed by
  // that would then print "You do not have access to open this" -- a sentence
  // this component cannot support and, for a POSP, one that is provably
  // false: the backend's own `posp` provider (core/search/providers.py) only
  // RETURNS these rows to a viewer who holds channels read, so the server has
  // already said yes by the time the row is on screen. The cell still fails
  // closed; `accessFor` below just keeps "we could not ask" apart from
  // "you may not".
  const {
    allowed: canReadChannels,
    isLoading: rightsLoading,
    isError: rightsCheckFailed,
    retry: retryRightsCheck,
  } = useCanState('channels', 'read');
  const hrefFor = useCallback(
    (result: GlobalSearchResult | undefined) => resultHref(result, undefined, canReadChannels),
    [canReadChannels]
  );

  /**
   * WHY a row cannot be opened, which is not the same question as whether it
   * can.
   *
   * `refused` is definite: the route's role list turns this viewer away
   * whatever the rights answer is, so saying so is honest. `unknown` is the
   * case where the ONLY thing standing between the reader and the record is a
   * rights answer that never arrived -- re-asking with the cell granted
   * returns an href -- and the row must say that instead of inventing a
   * refusal. `checking` is the same gap on the first render, before the
   * answer has had a chance to land.
   */
  type RowAccess = 'open' | 'refused' | 'checking' | 'unknown';
  const accessFor = useCallback(
    (result: GlobalSearchResult): RowAccess => {
      if (hrefFor(result) !== null) return 'open';
      const closedOnlyByChannelsCell = resultHref(result, undefined, true) !== null;
      if (!closedOnlyByChannelsCell) return 'refused';
      if (rightsCheckFailed) return 'unknown';
      if (rightsLoading) return 'checking';
      return 'refused';
    },
    [hrefFor, rightsCheckFailed, rightsLoading]
  );

  // Only worth a banner if the failure actually costs the reader something on
  // THIS result set: a failed rights fetch during a policy-only search closes
  // nothing, and an alert about it would be noise.
  const hasRightsBlockedRows = useMemo(
    () => rightsCheckFailed && flatResults.some((result) => accessFor(result) === 'unknown'),
    [rightsCheckFailed, flatResults, accessFor]
  );

  // A new result set invalidates the old highlight: index 3 of the previous
  // response is a different record. Clamp rather than blindly reset so a list
  // that merely shrank keeps a sensible position.
  useEffect(() => {
    setActiveIndex((current) => (current < 0 ? -1 : clampSelection(flatResults.length, current)));
  }, [flatResults]);

  const hasQuery = term.trim().length > 0;
  const showPanel = (open || sheetOpen) && hasQuery;

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const pick = useCallback(
    (result: GlobalSearchResult | undefined) => {
      if (!result) return;
      const href = hrefFor(result);
      if (!href) return;
      setTerm('');
      setOpen(false);
      setSheetOpen(false);
      setActiveIndex(-1);
      navigate(href);
    },
    [hrefFor, navigate]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!hasQuery) return;
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) =>
          moveSelection(flatResults.length, current, event.key === 'ArrowDown' ? 1 : -1)
        );
        return;
      }

      if (event.key === 'Enter') {
        if (!showPanel || flatResults.length === 0) return;
        event.preventDefault();
        pick(flatResults[activeIndex >= 0 ? activeIndex : 0]);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        if (sheetOpen) {
          closeSheet();
          return;
        }
        if (showPanel) {
          close();
          return;
        }
        // Panel already dismissed: a second Escape empties the field, which is
        // how every other "type to filter" control in the app behaves.
        setTerm('');
        return;
      }

      if (event.key === 'Home' && showPanel && flatResults.length > 0) {
        event.preventDefault();
        setActiveIndex(0);
        return;
      }

      if (event.key === 'End' && showPanel && flatResults.length > 0) {
        event.preventDefault();
        setActiveIndex(flatResults.length - 1);
      }
    },
    [activeIndex, close, closeSheet, flatResults, hasQuery, pick, sheetOpen, showPanel]
  );

  // Dismiss on a click outside. `mousedown` (not `click`) so the panel is gone
  // before a click on the page behind it lands.
  useEffect(() => {
    if (!showPanel) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const node = containerRef.current;
      if (node && event.target instanceof Node && !node.contains(event.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [showPanel]);

  // Keep the highlighted row visible when arrowing past the fold.
  useEffect(() => {
    if (activeIndex < 0) return;
    const node = document.getElementById(`${baseId}-option-${activeIndex}`);
    // jsdom has no scrollIntoView.
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, baseId]);

  useEffect(() => {
    if (sheetOpen) inputRef.current?.focus();
  }, [sheetOpen]);

  // Close the mobile sheet once the viewport reaches `sm`. The inline field is
  // suppressed while the sheet owns the input, and the sheet itself is
  // `sm:hidden` -- so rotating a tablet or widening the window with the sheet
  // open left NO search control in the header until a reload.
  const isSmUp = useMediaQuery('(min-width: 640px)');
  useEffect(() => {
    if (isSmUp && sheetOpen) setSheetOpen(false);
  }, [isSmUp, sheetOpen]);

  const activeDescendant =
    showPanel && activeIndex >= 0 ? `${baseId}-option-${activeIndex}` : undefined;

  const panel = (
    <div
      className={cn(
        'absolute left-0 right-0 top-full mt-2 z-50 overflow-hidden rounded-xl',
        'bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-gray-700'
      )}
    >
      <div className="max-h-[min(70vh,28rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
        {tooShort ? (
          <p className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">
            Keep typing — at least {GLOBAL_SEARCH_MIN_LENGTH} characters.
          </p>
        ) : error ? (
          <p className="px-4 py-6 text-sm text-center text-red-600 dark:text-red-400">{error}</p>
        ) : flatResults.length > 0 ? (
          <>
            {hasRightsBlockedRows && (
              // Outside the listbox on purpose: a button inside a role="option"
              // row is not reachable as a button for assistive tech, and one
              // retry per row would repeat a control that refetches the single
              // shared `/employee-rights/me/` query anyway.
              <div
                role="alert"
                className="mx-4 mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
              >
                Permissions could not be loaded, so some results cannot be opened. This is a
                connection problem, not a refusal.{' '}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => retryRightsCheck?.()}
                >
                  Retry
                </button>
              </div>
            )}
          <ul role="listbox" id={listboxId} aria-label="Search results" className="py-2">
            {groups.map((group, groupIndex) => {
              const groupLabelId = `${baseId}-group-${group.key}`;
              return (
                <li key={group.key} role="presentation">
                  <p
                    id={groupLabelId}
                    className="flex items-center gap-2 px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                  >
                    {group.label}
                    <Badge size="sm" className="dark:bg-gray-700 dark:text-gray-300">
                      {group.results.length}
                    </Badge>
                  </p>
                  <ul role="group" aria-labelledby={groupLabelId}>
                    {group.results.map((result, indexInGroup) => {
                      const flatIndex = offsets[groupIndex] + indexInGroup;
                      const isActive = flatIndex === activeIndex;
                      // A row that cannot be opened says so: one that looks
                      // clickable and then does nothing reads as a broken
                      // search. What it says depends on WHY -- see `accessFor`.
                      const access = accessFor(result);
                      const navigable = access === 'open';
                      return (
                        <li
                          key={resultKey(result, flatIndex)}
                          id={`${baseId}-option-${flatIndex}`}
                          role="option"
                          aria-selected={isActive}
                          aria-disabled={navigable ? undefined : true}
                          // Keep focus in the input so the combobox keeps
                          // receiving arrow keys while the mouse hovers.
                          onMouseDown={(event) => event.preventDefault()}
                          onMouseEnter={() => setActiveIndex(flatIndex)}
                          onClick={() => pick(result)}
                          className={cn(
                            'flex items-start gap-3 px-4 py-2',
                            navigable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
                            isActive
                              ? 'bg-primary-50 dark:bg-primary-900/30'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          )}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                              {result.label}
                            </span>
                            {access === 'open' ? null : access === 'unknown' ? (
                              <span className="block text-xs text-amber-700 dark:text-amber-500">
                                Permissions could not be loaded — retry above to open this
                              </span>
                            ) : access === 'checking' ? (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">
                                Checking your access…
                              </span>
                            ) : (
                              <span className="block text-xs text-amber-700 dark:text-amber-500">
                                You do not have access to open this
                              </span>
                            )}
                            {result.sublabel ? (
                              <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                {result.sublabel}
                              </span>
                            ) : null}
                          </span>
                          {result.status ? (
                            <span className="shrink-0 text-xs capitalize text-gray-400 dark:text-gray-500">
                              {resultStatusLabel(result.status)}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
          </>
        ) : isLoading ? (
          <p className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
            <LoadingSpinner size="sm" />
            Searching…
          </p>
        ) : hasSearched ? (
          <div className="px-4 py-8 text-center">
            <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
            <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No matches for “{resultTerm || term.trim()}”
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Try a policy or claim number, a name, a phone number or a vehicle number.
            </p>
          </div>
        ) : null}
      </div>

      {flatResults.length > 0 && (
        <p className="hidden sm:block border-t border-gray-100 dark:border-gray-700 px-4 py-2 text-[11px] text-gray-400 dark:text-gray-500">
          ↑↓ to navigate · ↵ to open · esc to dismiss
        </p>
      )}
    </div>
  );

  const field = (
    <div className="relative w-full" ref={containerRef}>
      <Input
        ref={inputRef}
        id={`${baseId}-input`}
        type="text"
        autoComplete="off"
        spellCheck={false}
        value={term}
        placeholder={placeholder}
        aria-label="Search all modules"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls={showPanel ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendant}
        onChange={(event) => {
          setTerm(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
        className={cn(
          'py-2 text-sm bg-white dark:bg-gray-900 dark:text-white dark:placeholder-gray-500',
          'dark:border-gray-600 dark:hover:border-gray-500',
          // Room for the clear button, which is positioned rather than passed
          // as Input's rightIcon (rightIcon is pointer-transparent decoration).
          hasQuery && 'pr-9'
        )}
      />
      {hasQuery && (
        <button
          type="button"
          aria-label="Clear search"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setTerm('');
            setActiveIndex(-1);
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
      {showPanel && panel}
    </div>
  );

  return (
    <>
      {/* Inline field: sm and up. Hidden while the mobile sheet owns the input
          so only one of the two is ever mounted. */}
      {!sheetOpen && (
        <div className={cn('hidden sm:block flex-1 min-w-0 max-w-lg', className)}>{field}</div>
      )}

      {/* Mobile trigger */}
      {!sheetOpen && (
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-label="Search"
          className="sm:hidden p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
        >
          <MagnifyingGlassIcon className="w-6 h-6" />
        </button>
      )}

      {/* Mobile sheet */}
      {sheetOpen && (
        <div
          className="fixed inset-0 z-50 sm:hidden bg-gray-900/40 dark:bg-gray-900/70"
          // Tapping the dimmed area dismisses the sheet, like every other
          // overlay in the app. Guarded on the target so a tap inside the panel
          // does not close it.
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeSheet();
          }}
        >
          <div className="bg-white dark:bg-gray-800 p-3 shadow-lg">
            <div className="flex items-center gap-2">
              {field}
              <button
                type="button"
                onClick={closeSheet}
                className="shrink-0 px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
